import { Trash2, RotateCcw } from "lucide-react";
import { Param } from "@/components/editor/Section";
import { ClipAudioControls } from "@/components/editor/ClipAudioControls";
import { LayerMotionControls } from "@/components/editor/LayerMotionControls";
import { layerAt, kfSet, hasKfs, kfIndexAt } from "@/lib/layerMotion";

export const FX_DEFAULT = { blend: "screen", opacity: 1, fade_in: 0, fade_out: 0, x: 0, y: 0, scale: 1, rot: 0, key: "#00ff00", sim: 0.12, smooth: 0.08, spill: 0.5 };
const BLENDS = [["screen", "Screen (VFX on black)"], ["chroma", "Chroma key (green screen)"], ["normal", "Normal"]];
export const TRANS = [["none", "Cut"], ["dissolve", "Cross dissolve"], ["dip", "Dip to black"], ["wipeleft", "Wipe left"], ["wiperight", "Wipe right"], ["slideleft", "Slide left"], ["slideright", "Slide right"], ["flash", "Celestial flash"], ["leak", "Golden light leak"], ["flare", "Lens-flare sweep"], ["starburst", "Starburst bloom"]];

const Pills = ({ options, value, onChange, prefix }) => (
  <div className="flex flex-wrap gap-1 mb-2">
    {options.map(([k, l]) => (
      <button key={k} onClick={() => onChange(k)} data-testid={`${prefix}-${k}`}
        className={`text-[11px] px-2 py-1 rounded transition-colors ${value === k ? "bg-[#EC4899] text-white" : "bg-[#0B0C10] border border-[#2A2E3D] text-slate-300 hover:border-[#EC4899]"}`}>{l}</button>
    ))}
  </div>
);

const FxControls = ({ clip, set, lt, onSeek }) => {
  const f = clip.fx, v = layerAt(f, lt), on = kfIndexAt(f, lt) >= 0;
  const p = (k) => (val) => set({ [k]: val });
  const q = (k) => (val) => set(kfSet(f, +lt.toFixed(3), { [k]: val }));
  return (
    <>
      <Pills options={BLENDS} value={f.blend} onChange={p("blend")} prefix="fx-blend" />
      <Param label="Opacity" value={v.opacity} min={0} max={1} onChange={q("opacity")} testId="fx-opacity" />
      <Param label="Fade in (s)" value={f.fade_in} min={0} max={Math.min(5, clip.len)} step={0.05} onChange={p("fade_in")} testId="fx-fade-in" />
      <Param label="Fade out (s)" value={f.fade_out} min={0} max={Math.min(5, clip.len)} step={0.05} onChange={p("fade_out")} testId="fx-fade-out" />
      <div className="flex items-center justify-between mt-3 mb-1">
        <span className="text-[10px] uppercase font-mono-ui tracking-wider text-slate-500">{!hasKfs(f) ? "Transform" : on ? "On keyframe" : "Between keyframes"} · drag the box in the viewer</span>
        <button onClick={() => set(kfSet(f, +lt.toFixed(3), { x: 0, y: 0, scale: 1, rot: 0 }))} className="text-slate-500 hover:text-[#EC4899]" data-testid="fx-transform-reset"><RotateCcw className="w-3 h-3" /></button>
      </div>
      <Param label="Position X" value={v.x} min={-1.5} max={1.5} onChange={q("x")} testId="fx-pos-x" />
      <Param label="Position Y" value={v.y} min={-1.5} max={1.5} onChange={q("y")} testId="fx-pos-y" />
      <Param label="Scale" value={v.scale} min={0.05} max={3} onChange={q("scale")} testId="fx-scale" />
      <Param label="Rotation" value={v.rot} min={-180} max={180} step={1} onChange={q("rot")} testId="fx-rotation" unit="°" />
      <LayerMotionControls clip={clip} set={set} lt={lt} onSeek={onSeek} />
      {f.blend === "chroma" && (
        <>
          <div className="flex items-center gap-2 mt-3 mb-1">
            <span className="text-[10px] uppercase font-mono-ui tracking-wider text-slate-500 flex-1">Key colour</span>
            {[["#00ff00", "Green"], ["#0047bb", "Blue"]].map(([c, l]) => (
              <button key={c} onClick={() => set({ key: c })} data-testid={`fx-key-${l.toLowerCase()}`}
                className={`text-[10px] px-2 py-0.5 rounded border ${f.key === c ? "border-white" : "border-[#2A2E3D]"}`} style={{ color: c }}>{l}</button>
            ))}
            <input type="color" value={f.key} onChange={(e) => set({ key: e.target.value })} className="w-6 h-6 bg-transparent" data-testid="fx-key-picker" />
          </div>
          <Param label="Similarity" value={f.sim} min={0} max={0.5} step={0.005} onChange={p("sim")} testId="fx-key-similarity" />
          <Param label="Edge softness" value={f.smooth} min={0} max={0.3} step={0.005} onChange={p("smooth")} testId="fx-key-smoothness" />
          <Param label="Spill removal" value={f.spill} min={0} max={1} onChange={p("spill")} testId="fx-key-spill" />
        </>
      )}
    </>
  );
};

export const ClipPanel = ({ clip, name, onPatch, onRemove, onClose, lt, onSeek }) => (
  <div className="border-b-2 border-[#EC4899]/40 bg-[#181A22] px-4 py-3 max-h-[65%] overflow-y-auto shrink-0" data-testid="clip-panel">
    <div className="flex items-center justify-between mb-2">
      <p className="text-xs font-semibold truncate"><span className="text-[#EC4899] font-mono-ui text-[10px] mr-2">{clip.track === "fx" ? "V2 LAYER" : "V1 CLIP"}</span>{name}</p>
      <div className="flex gap-2">
        <button onClick={onRemove} className="text-slate-500 hover:text-red-400" data-testid="clip-panel-remove"><Trash2 className="w-3.5 h-3.5" /></button>
        <button onClick={onClose} className="text-[11px] text-slate-400 hover:text-white" data-testid="clip-panel-close">Done</button>
      </div>
    </div>
    {clip.track === "fx" && <FxControls clip={clip} lt={lt} onSeek={onSeek} set={(patch) => onPatch({ fx: { ...clip.fx, ...patch } })} />}
    {clip.track === "video" && (clip.idx > 0 ? (
      <>
        <p className="text-[10px] uppercase font-mono-ui tracking-wider text-slate-500 mb-2">Transition into this clip</p>
        <Pills options={TRANS} value={clip.trans?.type || "none"} onChange={(type) => onPatch({ trans: { dur: clip.trans?.dur || 1, type } })} prefix="transition" />
        {clip.trans?.type && clip.trans.type !== "none" && (
          <Param label="Duration (s)" value={clip.trans.dur} min={0.1} max={4} step={0.05} onChange={(dur) => onPatch({ trans: { ...clip.trans, dur } })} testId="transition-duration" />
        )}
      </>
    ) : <p className="text-[11px] text-slate-500">First clip on the storyline. Select a later clip to add a transition into it.</p>)}
    {clip.track === "video" && <ClipAudioControls clip={clip} lt={lt} onPatch={onPatch} />}
  </div>
);
